import { z } from "zod";

// Search-param schemas for routes that accept ?key=value filters in the URL.
// Wire these onto each route's `validateSearch` so `useSearch({ from: ... })`
// returns a typed object instead of needing a cast at the call site.

/** Filters for the Claims MRP page (`/claims/mrp`). All optional, all coming
 *  from the URL as raw strings. The boolean filters use string `"true"` rather
 *  than parsing to a real boolean — the API expects the literal string "true"
 *  (anything else is treated as false). */
export const claimsMrpSearchSchema = z.object({
  policyNumber: z.string().optional(),
  agentNumber: z.string().optional(),
  isOpenIndemnity: z.string().optional(),
  isOpenMedicalOnly: z.string().optional(),
  isClaimOpen: z.string().optional(),
  /** Exact `loss_type_description` to scope to (e.g. "Indemnity",
   *  "Medical Only"). Composable with isClaimOpen — unlike isOpenIndemnity,
   *  this does NOT imply an open status, so it matches claims at any status. */
  lossType: z.string().optional(),
  /** Parent-context hint for breadcrumb trail. See @/lib/breadcrumb-from. */
  from: z.string().optional(),
});

export type ClaimsMrpSearch = z.infer<typeof claimsMrpSearchSchema>;

/** Filters for the Policy MRP page (`/policy/list`). */
export const policyMrpSearchSchema = z.object({
  agencyCode: z.string().optional(),
  activePolicyBind: z.string().optional(),
  /** Parent-context hint for breadcrumb trail. See @/lib/breadcrumb-from. */
  from: z.string().optional(),
});

export type PolicyMrpSearch = z.infer<typeof policyMrpSearchSchema>;

/** Optional deep-link params for the Policy Dashboard. `term` is an
 *  effective-date string (ISO `YYYY-MM-DD`) that selects which policy term
 *  the dashboard opens to — used by the Loss Ratio per-term table to link
 *  back to a specific term. */
export const policyDashboardSearchSchema = z.object({
  term: z.string().optional(),
  /** Parent-context hint for breadcrumb trail. See @/lib/breadcrumb-from. */
  from: z.string().optional(),
});

export type PolicyDashboardSearch = z.infer<typeof policyDashboardSearchSchema>;

/** Optional params for the Claim Dashboard. */
export const claimsDashboardSearchSchema = z.object({
  /** Parent-context hint for breadcrumb trail. See @/lib/breadcrumb-from. */
  from: z.string().optional(),
});

export type ClaimsDashboardSearch = z.infer<typeof claimsDashboardSearchSchema>;
